import {
  Alert,
  Badge,
  Breadcrumb,
  Button,
  Checkbox,
  DescriptionList,
  Dropdown,
  EmptyState,
  FieldGroup as FormFieldGroup,
  formatDate,
  formatPercent,
  FormField,
  Grid,
  IconButton,
  Inline,
  InlineItem,
  Input,
  Modal,
  Money,
  Panel,
  Select,
  SkeletonText,
  SortableList,
  Stack,
  Text,
  useToast,
  VisuallyHidden,
} from '@hkdevs/design-system';
import { useBlocker } from '@tanstack/react-router';
import { useId, useState, type ReactNode } from 'react';
import { ApiError } from '../../api/client';
import {
  useProduct,
  useProductPreview,
  useRenameProduct,
  useSaveFields,
  useSourceColumns,
  type FieldFormat,
  type ProductType,
} from '../../api/queries/products';
import { NotFound } from '../../shell/Page';
import { PageLayout } from '../../shell/Page';
import { RouterLink } from '../../shell/RouterLink';
import { fieldCountLabel } from './ProductsPage';
import { withReference } from '../../api/withReference';

/**
 * A product type's page (pc-dbb.20, pc-50i.5): its line-item fields in the order a check
 * shows them, reorderable by keyboard or pointer, with Add field, Edit and Remove
 * (confirmed), and a preview of a line as a check shows it. Changes stay in a draft until
 * Save changes; leaving with unsaved changes asks first.
 */

/** A field in the draft: a saved one keeps its id; a new one has a `new-` id until saved. */
export interface DraftField {
  id: string;
  label: string;
  sourceColumn: string;
  format: FieldFormat;
  group: string;
  total: boolean;
}

export type FieldFormValues = Omit<DraftField, 'id'>;

export const FIELD_FORMATS: { value: FieldFormat; label: string }[] = [
  { value: 'text', label: 'General' },
  { value: 'number', label: 'Number' },
  { value: 'decimal', label: 'Decimal' },
  { value: 'money', label: 'Money' },
  { value: 'date', label: 'Date' },
  { value: 'percent', label: 'Percentage' },
];

const formatLabel = (format: FieldFormat) =>
  FIELD_FORMATS.find((option) => option.value === format)?.label ?? format;

/** The titled groups a line item's fields show in, on a check. */
export const FIELD_GROUPS = ['Deal & Customer', 'Pay Math', 'Details'] as const;

export const emptyField: FieldFormValues = {
  label: '',
  sourceColumn: '',
  format: 'text',
  group: 'Deal & Customer',
  total: false,
};

const isNew = (id: string) => id.startsWith('new-');

/** The fields with the one at `from` moved to `to`. */
export function moveField<T>(items: readonly T[], from: number, to: number): T[] {
  const next = [...items];
  const [moved] = next.splice(from, 1);
  next.splice(to, 0, moved!);
  return next;
}

/** The draft differs from what's saved: a field added, removed, changed or moved. */
export const isDirty = (saved: readonly DraftField[], draft: readonly DraftField[]) =>
  JSON.stringify(saved) !== JSON.stringify(draft);

/** A source column the latest upload has, ignoring case and spaces around it. */
export const hasColumn = (columns: readonly string[], column: string) =>
  columns.some((known) => known.trim().toLowerCase() === column.trim().toLowerCase());

export interface FieldFormErrors {
  label?: string;
  sourceColumn?: string;
}

/** What's wrong with a field, given the type's other fields' labels. */
export function validateField(
  values: FieldFormValues,
  otherLabels: readonly string[],
): FieldFormErrors {
  const errors: FieldFormErrors = {};
  const label = values.label.trim();
  if (!label) errors.label = 'Enter a label.';
  else if (otherLabels.some((other) => other.trim().toLowerCase() === label.toLowerCase()))
    errors.label = `This product type already has a field called ${label}.`;
  if (!values.sourceColumn.trim())
    errors.sourceColumn = 'Enter the upload column this field comes from.';
  return errors;
}

/** The fields with this one added or replaced, trimmed. Marking it the total unmarks the others. */
export function applyField(
  fields: readonly DraftField[],
  values: FieldFormValues,
  id: string,
): DraftField[] {
  const field: DraftField = {
    ...values,
    id,
    label: values.label.trim(),
    sourceColumn: values.sourceColumn.trim(),
  };
  const others = values.total ? fields.map((f) => (f.total ? { ...f, total: false } : f)) : fields;
  return others.some((f) => f.id === id)
    ? others.map((f) => (f.id === id ? field : f))
    : [...others, field];
}

/** The groups in the order of their first field, each with its fields in order. */
export function groupFields(fields: readonly DraftField[]) {
  const groups = new Map<string, DraftField[]>();
  for (const field of fields) groups.set(field.group, [...(groups.get(field.group) ?? []), field]);
  return [...groups].map(([title, inGroup]) => ({ title, fields: inGroup }));
}

/** An example value per format, while there's no upload to preview. */
const EXAMPLES: Record<FieldFormat, string> = {
  text: 'Text',
  number: '12',
  decimal: '9.6',
  money: '1240',
  date: '2023-01-03',
  percent: '15',
};

/** A field's value as a check shows it: from the upload's row, or an example. */
export function previewValue(
  field: DraftField,
  sample: Readonly<Record<string, string>> | null,
): ReactNode {
  const key = sample
    ? Object.keys(sample).find((column) => hasColumn([column], field.sourceColumn))
    : undefined;
  const raw = sample ? (key === undefined ? undefined : sample[key]) : EXAMPLES[field.format];
  if (raw === undefined || raw.trim() === '') return '—';
  const number = Number(raw);
  switch (field.format) {
    case 'money':
      return Number.isFinite(number) ? <Money value={raw} /> : raw;
    case 'number':
      return Number.isFinite(number) ? Math.round(number).toLocaleString('en-US') : raw;
    case 'decimal':
      return Number.isFinite(number)
        ? number.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
        : raw;
    case 'date': {
      const date = formatDate(raw);
      return date === '—' ? raw : date;
    }
    case 'percent': {
      const percent = percentOf(raw);
      return percent === null ? raw : formatPercent(percent);
    }
    default:
      return raw;
  }
}

/** A sheet's percentage as the API reads it (ADR 0030): "15" and "15%" are 15, and a bare
 * number below 1 is a fraction ("0.15" is 15). Null when it isn't a number. */
export function percentOf(raw: string): number | null {
  const text = raw.trim().replaceAll(',', '');
  const marked = text.endsWith('%');
  const body = marked ? text.slice(0, -1).trim() : text;
  const number = body === '' ? Number.NaN : Number(body);
  if (!Number.isFinite(number)) return null;
  return marked || Math.abs(number) >= 1 ? number : number * 100;
}

/** The saved fields as a draft. */
const draftOf = (product: ProductType): DraftField[] =>
  product.fields.map(({ id, label, sourceColumn, format, group, total }) => ({
    id,
    label,
    sourceColumn,
    format,
    group,
    total,
  }));

/** The product type's page, once it's loaded: the draft lives here. */
export function ProductTypePage({ productId }: { productId: string }) {
  const product = useProduct(productId);

  if (product.error instanceof ApiError && product.error.status === 404) {
    return <NotFound home="/products" />;
  }
  const breadcrumb = (name: string) => (
    <Breadcrumb
      items={[{ label: 'Products', href: '/products', as: RouterLink }, { label: name }]}
    />
  );
  if (product.isError) {
    return (
      <PageLayout page="products" title="Product type" breadcrumb={breadcrumb('…')}>
        <Alert
          tone="danger"
          title="The product type couldn’t be loaded"
          action={
            <Button size="sm" variant="secondary" onClick={() => void product.refetch()}>
              Retry
            </Button>
          }
        >
          Check your connection and try again.
        </Alert>
      </PageLayout>
    );
  }
  if (!product.data) {
    return (
      <PageLayout page="products" title="Product type" breadcrumb={breadcrumb('…')}>
        <SkeletonText lines={6} />
        <VisuallyHidden role="status">Loading the product type</VisuallyHidden>
      </PageLayout>
    );
  }
  // A new draft whenever the saved fields change (a rename keeps the draft).
  return (
    <ProductTypeEditor
      key={JSON.stringify(product.data.fields)}
      product={product.data}
      breadcrumb={breadcrumb(product.data.name)}
    />
  );
}

let newIds = 0;

function ProductTypeEditor({
  product,
  breadcrumb,
}: {
  product: ProductType;
  breadcrumb: ReactNode;
}) {
  const { toast } = useToast();
  const saved = draftOf(product);
  const [fields, setFields] = useState<DraftField[]>(saved);
  const save = useSaveFields();
  const columns = useSourceColumns(product.id);
  const preview = useProductPreview(product.id);
  const dirty = isDirty(saved, fields);
  const saving = save.isPending;
  const [renaming, setRenaming] = useState(false);
  const blocker = useBlocker({
    shouldBlockFn: () => dirty && !saving,
    enableBeforeUnload: () => dirty,
    withResolver: true,
  });

  const latest = columns.data?.uploadedAt
    ? {
        uploadedOn: columns.data.uploadedAt,
        columns: columns.data.columns,
        sample: preview.data?.sample ?? null,
      }
    : null;

  // mutateAsync, not mutate's callbacks: a save remounts this editor (its key is the
  // saved fields), and mutate skips the callbacks of an unmounted caller.
  const onSave = () =>
    save
      .mutateAsync({
        id: product.id,
        fields: fields.map(({ id, ...values }) => ({ ...(isNew(id) ? {} : { id }), ...values })),
      })
      .then(
        () => toast({ tone: 'success', title: `${product.name} is saved` }),
        (error: unknown) =>
          toast(
            withReference(
              {
                tone: 'danger',
                title: `${product.name} couldn’t be saved`,
                description:
                  error instanceof ApiError && error.problem.errors?.length
                    ? error.problem.errors[0]!.message
                    : 'Check your connection and try again.',
              },
              error,
            ),
          ),
      );

  return (
    <PageLayout
      page="products"
      title={product.name}
      subtitle={fieldCountLabel(fields.length)}
      breadcrumb={breadcrumb}
      actions={
        <>
          {dirty && (
            <Badge tone="warning" dot>
              Unsaved changes
            </Badge>
          )}
          {dirty && (
            <Button
              size="sm"
              variant="secondary"
              onClick={() => setFields(saved)}
              disabled={saving}
            >
              Cancel
            </Button>
          )}
          <Dropdown
            align="end"
            trigger={
              <IconButton
                icon="more-vertical"
                size="sm"
                variant="secondary"
                aria-label={`More actions for ${product.name}`}
              />
            }
            items={[{ label: 'Rename', onClick: () => setRenaming(true), disabled: saving }]}
          />
          <Button
            size="sm"
            onClick={() => void onSave()}
            disabled={!dirty && !saving}
            loading={saving}
            loadingText="Saving…"
          >
            Save changes
          </Button>
        </>
      }
    >
      <FieldsAndPreview
        productName={product.name}
        fields={fields}
        onFieldsChange={setFields}
        latest={latest}
        saving={saving}
      />
      {renaming && <RenameModal product={product} onClose={() => setRenaming(false)} />}
      <Modal
        open={blocker.status === 'blocked'}
        onClose={() => blocker.reset?.()}
        size="sm"
        title={`Discard your changes to ${product.name}?`}
        description="You haven’t saved them, so checks still show its fields as they were."
        footer={
          <>
            <Button variant="secondary" onClick={() => blocker.reset?.()}>
              Keep editing
            </Button>
            <Button variant="danger" onClick={() => blocker.proceed?.()}>
              Discard changes
            </Button>
          </>
        }
      />
    </PageLayout>
  );
}

interface LatestUpload {
  /** An instant. */
  uploadedOn: string;
  columns: readonly string[];
  sample: Readonly<Record<string, string>> | null;
}

type Editing = { kind: 'add' } | { kind: 'edit'; id: string } | null;

function FieldsAndPreview({
  productName,
  fields,
  onFieldsChange,
  latest,
  saving,
}: {
  productName: string;
  fields: readonly DraftField[];
  onFieldsChange: (fields: DraftField[]) => void;
  latest: LatestUpload | null;
  saving: boolean;
}) {
  const [editing, setEditing] = useState<Editing>(null);
  const [opening, setOpening] = useState(0);
  const [removingId, setRemovingId] = useState<string | null>(null);
  const removing = fields.find((field) => field.id === removingId);
  const editingField =
    editing?.kind === 'edit' ? fields.find((field) => field.id === editing.id) : undefined;
  const missing = (field: DraftField) => !!latest && !hasColumn(latest.columns, field.sourceColumn);
  const edit = (next: Editing) => {
    setEditing(next);
    setOpening((n) => n + 1);
  };

  const addButton = (
    <Button
      size="sm"
      variant="secondary"
      leadingIcon="plus"
      disabled={saving}
      onClick={() => edit({ kind: 'add' })}
    >
      Add field
    </Button>
  );

  return (
    <>
      <Grid minColumnWidth="xl" gap={4} align="start">
        <Panel
          title="Fields"
          meta={fieldCountLabel(fields.length)}
          actions={fields.length > 0 && addButton}
        >
          {fields.length === 0 ? (
            <EmptyState
              title="No fields yet"
              description={`Add the fields a ${productName} line shows on a check, and the upload column each comes from.`}
              icon="file"
              headingLevel={3}
              actions={addButton}
            />
          ) : (
            <SortableList
              label={`${productName} fields, in the order a check shows them`}
              items={fields}
              getKey={(field) => field.id}
              getLabel={(field) => field.label}
              disabled={saving}
              onReorder={(from, to) => onFieldsChange(moveField(fields, from, to))}
              renderItem={(field) => (
                <Inline as="span" gap={2} wrap={false}>
                  <InlineItem as="span" grow>
                    <Stack as="span" gap={1}>
                      <Inline as="span" gap={2} rowGap={1}>
                        <Text weight="semibold">{field.label}</Text>
                        {field.total && (
                          <Badge size="sm" tone="neutral">
                            Total
                          </Badge>
                        )}
                        {missing(field) && (
                          <Badge size="sm" tone="warning">
                            Not in the latest upload
                          </Badge>
                        )}
                      </Inline>
                      <Text tone="muted" size="meta">
                        <VisuallyHidden>Column </VisuallyHidden>
                        <Text mono>{field.sourceColumn}</Text>
                        {` · ${formatLabel(field.format)} · ${field.group}`}
                      </Text>
                    </Stack>
                  </InlineItem>
                  <Dropdown
                    align="end"
                    trigger={
                      <IconButton
                        icon="more-vertical"
                        size="sm"
                        variant="ghost"
                        disabled={saving}
                        aria-label={`Actions for ${field.label}`}
                      />
                    }
                    items={[
                      { label: 'Edit', onClick: () => edit({ kind: 'edit', id: field.id }) },
                      { label: 'Remove', danger: true, onClick: () => setRemovingId(field.id) },
                    ]}
                  />
                </Inline>
              )}
            />
          )}
        </Panel>
        <Panel title="Preview" meta={`A ${productName} line on a check`}>
          <Preview fields={fields} latest={latest} />
        </Panel>
      </Grid>

      {editing !== null && (
        <FieldEditor
          key={opening}
          onClose={() => setEditing(null)}
          productName={productName}
          initialValues={editingField ?? emptyField}
          editing={!!editingField}
          otherLabels={fields.filter((f) => f !== editingField).map((f) => f.label)}
          currentTotal={fields.find((f) => f.total && f !== editingField)?.label}
          latest={latest}
          onSubmit={(values) => {
            newIds += 1;
            onFieldsChange(applyField(fields, values, editingField?.id ?? `new-${newIds}`));
            setEditing(null);
          }}
        />
      )}

      <Modal
        open={!!removing}
        onClose={() => setRemovingId(null)}
        size="sm"
        title={removing && `Remove “${removing.label}” from ${productName}?`}
        description={`When you save, ${productName} lines stop showing it on checks. The uploads keep its column.`}
        footer={
          removing && (
            <>
              <Button variant="secondary" onClick={() => setRemovingId(null)}>
                Cancel
              </Button>
              <Button
                variant="danger"
                onClick={() => {
                  onFieldsChange(fields.filter((field) => field.id !== removing.id));
                  setRemovingId(null);
                }}
              >
                Remove field
              </Button>
            </>
          )
        }
      />
    </>
  );
}

/** A line item of this type as a check shows it: a Panel per group. */
function Preview({
  fields,
  latest,
}: {
  fields: readonly DraftField[];
  latest: LatestUpload | null;
}) {
  if (fields.length === 0) {
    return (
      <Text as="p" tone="muted">
        Add a field to see it here.
      </Text>
    );
  }
  return (
    <Stack gap={3}>
      <Text as="p" tone="muted">
        {latest
          ? `With the first line of the upload on ${formatDate(latest.uploadedOn, 'date')}.`
          : 'With example values: there’s no upload of this type yet.'}
      </Text>
      {groupFields(fields).map((group) => (
        <Panel key={group.title} title={group.title} headingLevel={3}>
          <DescriptionList
            items={group.fields.map((field) => {
              const value = previewValue(field, latest ? latest.sample : null);
              return {
                key: field.id,
                term: field.total ? <strong>{field.label}</strong> : field.label,
                description: field.total ? <strong>{value}</strong> : value,
              };
            })}
          />
        </Panel>
      ))}
    </Stack>
  );
}

/** Add field, or Edit field: its label, source column, format, group and whether it's the total. */
function FieldEditor({
  onClose,
  productName,
  initialValues,
  editing,
  otherLabels,
  currentTotal,
  latest,
  onSubmit,
}: {
  onClose: () => void;
  productName: string;
  initialValues: FieldFormValues;
  editing: boolean;
  otherLabels: readonly string[];
  currentTotal: string | undefined;
  latest: LatestUpload | null;
  onSubmit: (values: FieldFormValues) => void;
}) {
  const [values, setValues] = useState<FieldFormValues>(initialValues);
  const [showErrors, setShowErrors] = useState(false);
  const errors = showErrors ? validateField(values, otherLabels) : {};
  const set = <K extends keyof FieldFormValues>(key: K, value: FieldFormValues[K]) =>
    setValues((current) => ({ ...current, [key]: value }));
  const column = values.sourceColumn.trim();
  const unknownColumn = !!latest && !!column && !hasColumn(latest.columns, column);
  const replacesId = useId();
  const formId = useId();
  const replacesTotal = !!currentTotal && values.total;
  const groups = FIELD_GROUPS.includes(values.group as (typeof FIELD_GROUPS)[number])
    ? FIELD_GROUPS
    : [...FIELD_GROUPS, values.group];

  const submit = () => {
    setShowErrors(true);
    if (Object.keys(validateField(values, otherLabels)).length) return;
    onSubmit(values);
  };

  return (
    <Modal
      open
      onClose={onClose}
      size="sm"
      title={editing ? 'Edit field' : 'Add field'}
      description={`A field ${productName} lines show on a check. It’s saved when you save ${productName}.`}
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" form={formId}>
            {editing ? 'Done' : 'Add field'}
          </Button>
        </>
      }
    >
      <FormFieldGroup
        as="form"
        id={formId}
        onSubmit={(event) => {
          event.preventDefault();
          submit();
        }}
      >
        <FormField label="Label" required error={errors.label} hint="As a check shows it.">
          <Input
            value={values.label}
            autoComplete="off"
            onChange={(event) => set('label', event.target.value)}
          />
        </FormField>
        <FormField
          label="Source column"
          required
          error={errors.sourceColumn}
          hint={
            unknownColumn
              ? `The upload on ${formatDate(latest.uploadedOn, 'date')} has no column called ${column}. Check the spelling; until an upload has it, checks show “—”.`
              : 'The upload spreadsheet’s column heading.'
          }
        >
          <Input
            value={values.sourceColumn}
            autoComplete="off"
            spellCheck={false}
            onChange={(event) => set('sourceColumn', event.target.value)}
          />
        </FormField>
        <FormField label="Format">
          <Select
            options={FIELD_FORMATS}
            value={values.format}
            onChange={(event) => set('format', event.target.value as FieldFormat)}
          />
        </FormField>
        <FormField label="Group" hint="The titled box it shows in on a check.">
          <Select
            options={groups.map((group) => ({ value: group, label: group }))}
            value={values.group}
            onChange={(event) => set('group', event.target.value)}
          />
        </FormField>
        <Stack gap={1}>
          <Checkbox
            label="It’s the line’s total (bold)"
            checked={values.total}
            aria-describedby={replacesTotal ? replacesId : undefined}
            onChange={(event) => set('total', event.target.checked)}
          />
          {replacesTotal && (
            <Text as="p" id={replacesId} tone="muted" size="meta">
              Instead of {currentTotal}: a line has one total.
            </Text>
          )}
        </Stack>
      </FormFieldGroup>
    </Modal>
  );
}

/** Rename: a new name for the type. Its line items are renamed too, so checks keep its fields. */
function RenameModal({ product, onClose }: { product: ProductType; onClose: () => void }) {
  const { toast } = useToast();
  const formId = useId();
  const rename = useRenameProduct();
  const [name, setName] = useState(product.name);
  const [error, setError] = useState<string | undefined>();

  const submit = () => {
    const trimmed = name.trim();
    if (!trimmed) {
      setError('Enter a name.');
      return;
    }
    setError(undefined);
    rename
      .mutateAsync({ id: product.id, name: trimmed })
      .then((renamed) => {
        toast({ tone: 'success', title: `${product.name} is now ${renamed.name}` });
        onClose();
      })
      .catch((failure: unknown) => {
        if (failure instanceof ApiError && failure.status === 409) {
          setError(failure.problem.errors?.[0]?.message ?? failure.message);
        } else {
          toast(
            withReference(
              {
                tone: 'danger',
                title: `${product.name} couldn’t be renamed`,
                description: 'Check your connection and try again.',
              },
              failure,
            ),
          );
        }
      });
  };

  return (
    <Modal
      open
      onClose={onClose}
      size="sm"
      title={`Rename ${product.name}`}
      description="Its line items take the new name, so checks keep its fields. A file that still uses the old name adds a new product type."
      dismissable={!rename.isPending}
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={rename.isPending}>
            Cancel
          </Button>
          <Button type="submit" form={formId} loading={rename.isPending} loadingText="Renaming…">
            Rename
          </Button>
        </>
      }
    >
      <form
        id={formId}
        onSubmit={(event) => {
          event.preventDefault();
          submit();
        }}
      >
        <FormField label="Name" required error={error}>
          <Input
            value={name}
            autoComplete="off"
            disabled={rename.isPending}
            onChange={(event) => setName(event.target.value)}
          />
        </FormField>
      </form>
    </Modal>
  );
}
